import { Icon } from '@iconify/react/dist/iconify.js';
import { TagList } from '@/components/common/tag';
import { useProfileDetails } from './hooks';
import type { Person } from '@/store/people/types';
import { Name, NameAvatar } from '@/components/common/output/name';
import { useTags } from '@/store/tags/hooks';
import type { Tag } from '@/shared/types/tag';
import { useMemo } from 'react';
import { cn } from '@/lib/cn';

interface ProfileAvatarCardProps {
  profileData?: Person['data'];
  isDrawer?: boolean;
  name?: string;
  email?: string;
  phone?: string;
  roles?: string[];
  imageUrl?: string;
  isActive?: boolean;
  tags?: Tag[];
  className?: string;
}

const ProfileAvatarCard: React.FC<ProfileAvatarCardProps> = ({
  profileData,
  isDrawer,
  className,
}) => {
  const profile = useProfileDetails(profileData);
  const { list: allTags } = useTags();

  const profileTags = useMemo(() => {
    if (!profileData?.tags) {
      return [];
    }

    return allTags.filter((tag) => profileData.tags?.[tag.id]);
  }, [profileData?.tags, allTags]);

  const fullName =
    profile.fullname || `${profile.firstName} ${profile.lastName}`;

  return (
    <div className={cn('w-full pt-2', className)}>
      <div
        className={`${isDrawer ? '' : 'rounded-lg border border-gray-100 bg-white shadow-md'}`}
      >
        <div className='px-4 pt-4'>
          <div
            className={`flex gap-2 md:gap-4 ${
              isDrawer ? 'flex-col items-start' : 'flex-row items-center'
            }`}
          >
            <div className=''>
              {isDrawer ? (
                <Name value={fullName} size='md' />
              ) : (
                <NameAvatar value={fullName} size='lg' />
              )}
            </div>
            <div className='flex flex-col gap-2'>
              {!isDrawer && (
                <div className='text-2xl font-bold text-gray-900'>
                  {fullName}
                </div>
              )}
              <div className='mb-2 flex flex-col items-start gap-2 md:flex-row md:items-center md:gap-4'>
                {profile.email && (
                  <p className='inline-flex items-center text-sm font-medium text-gray-600'>
                    <Icon icon='mdi:email-outline' className='mr-1' />{' '}
                    {profile.email}
                  </p>
                )}
                {profile.phone && (
                  <p className='inline-flex items-center text-sm font-medium text-gray-600'>
                    <Icon icon='mdi:phone-outline' className='mr-1' />{' '}
                    {profile.phone}
                  </p>
                )}
              </div>
              <TagList tags={profileTags} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileAvatarCard;
